import {
  AnnouncementGetByIdDocument,
  AnnouncementForHomepageByIdDocument,
  AnnouncementsByUserDocument,
  AnnouncementsPaginatedDocument,
  AnnouncementCreateDocument,
  AnnouncementDeleteDocument,
  AnnouncementDocumentDownloadDocument,
  AnnouncementUpdateDocument,
  AnnouncementDocumentUploadDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { IFile, NumberFiltersType } from "api/services/types";
import { Optional, PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  AnnouncementsForHomepageByIdResponse,
  AnnouncementsForUserResponse,
  AnnouncementsUpdateCreateResponse,
  IAnnouncementDocumentModel,
  IAnnouncementModel,
  IAnnouncementPaginatedResponse,
  IAnnouncementPaginatedVars,
  ICreateAnnouncementVars,
  IUpdateAnnouncementVars,
} from "./types";

export async function getAnnouncementsPaginated(
  variables: IAnnouncementPaginatedVars
): Promise<IAnnouncementPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AnnouncementsPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.announcementsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createAnnouncement(
  variables: ICreateAnnouncementVars
): Promise<Optional<AnnouncementsUpdateCreateResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AnnouncementCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Announcement",
      fieldNames: ["announcementsPaginated", "announcementsByUser"],
    });

    return res?.data?.announcementCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateAnnouncement(
  variables: IUpdateAnnouncementVars
): Promise<Optional<AnnouncementsUpdateCreateResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AnnouncementUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Announcement",
      id: variables.announcementId.toString(),
      fieldNames: [
        "announcementsPaginated",
        "announcementsByUser",
        "announcementGetById",
        "announcementViewForInvestor",
      ],
    });

    return res?.data?.announcementUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getAnnouncement(
  announcementId: number
): Promise<PartialNullable<IAnnouncementModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AnnouncementGetByIdDocument,
      variables: { announcementId },
      fetchPolicy: "network-only",
    });

    return res?.data?.announcementGetById;
  } catch (error) {
    throw error;
  }
}

export async function getAnnouncementsForUser(
  productFundId?: NumberFiltersType
): Promise<AnnouncementsForUserResponse[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AnnouncementsByUserDocument,
      variables: { productFundId },
      fetchPolicy: "network-only",
    });

    return res?.data?.announcementsByUser ?? [];
  } catch (error) {
    throw error;
  }
}

export async function deleteAnnouncement(
  announcementId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AnnouncementDeleteDocument,
      variables: { announcementId },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Announcement",
      id: announcementId.toString(),
      fieldNames: ["announcementsPaginated", "announcementsByUser"],
    });

    return Boolean(res?.data?.announcementDelete);
  } catch (error) {
    throw error;
  }
}

export async function uploadAnnouncementDocument(
  variables: IFile
): Promise<Optional<IAnnouncementDocumentModel<number>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AnnouncementDocumentUploadDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.relatedEntityId) {
      executeEviction(client, {
        typename: "Announcement",
        id: variables.relatedEntityId.toString(),
        fieldNames: ["announcementGetById", "announcementViewForInvestor"],
      });
    }

    return res?.data?.announcementDocumentUpload;
  } catch (error) {
    throw error;
  }
}

export async function downloadAnnouncementDocument(
  announcementDocumentId: number
): Promise<Optional<string>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AnnouncementDocumentDownloadDocument,
      variables: { announcementDocumentId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.announcementDocumentDownload;
  } catch (error) {
    throw error;
  }
}

export async function getAnnouncementForHomepage(
  announcementId: number
): Promise<AnnouncementsForHomepageByIdResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AnnouncementForHomepageByIdDocument,
      variables: { announcementId },
      fetchPolicy: "network-only",
    });

    return res?.data?.announcementViewForInvestor;
  } catch (error) {
    throw error;
  }
}
